import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../config/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { FiUpload, FiTrash2, FiImage, FiPlus } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const ManageGallery = () => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({ image: null, caption: '' });
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        fetchGallery();
    }, []);

    const fetchGallery = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/manage_gallery.php`);
            const data = await response.json();
            if (data.status === 'success') {
                setImages(data.data);
            }
        } catch (error) {
            console.error("Error fetching gallery:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!formData.image) return;

        setUploading(true);
        const data = new FormData();
        data.append('image', formData.image);
        data.append('caption', formData.caption);

        try {
            const response = await fetch(`${API_BASE_URL}/manage_gallery.php`, {
                method: 'POST',
                body: data
            });
            const result = await response.json();
            if (result.status === 'success') {
                alert("Image uploaded successfully!");
                setShowModal(false);
                setFormData({ image: null, caption: '' });
                fetchGallery();
            } else {
                alert(result.message);
            }
        } catch (error) {
            alert("Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this image?")) return;

        try {
            const response = await fetch(`${API_BASE_URL}/manage_gallery.php?id=${id}`, {
                method: 'DELETE'
            });
            const result = await response.json();
            if (result.status === 'success') {
                setImages(images.filter(img => img.id !== id));
            }
        } catch (error) {
            alert("Delete failed");
        }
    };

    return (
        <DashboardLayout title="Manage Gallery">
            <div className="p-6">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h2 className="text-3xl font-bold text-slate-900">Campus Gallery</h2>
                        <p className="text-slate-500">Add or remove images from the campus gallery.</p>
                    </div>
                    <button 
                        onClick={() => setShowModal(true)}
                        className="bg-rose-600 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2 hover:bg-rose-700 transition-colors shadow-lg shadow-rose-200"
                    >
                        <FiPlus /> Add Image
                    </button>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600"></div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        {images.map(img => (
                            <motion.div 
                                key={img.id}
                                layout
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="bg-white rounded-2xl overflow-hidden shadow-md border border-slate-100 relative group"
                            >
                                <div className="aspect-square relative">
                                    <img 
                                        src={`${API_BASE_URL}/../${img.image_path}`} 
                                        alt={img.caption}
                                        className="w-full h-full object-cover"
                                    />
                                    <button 
                                        onClick={() => handleDelete(img.id)}
                                        className="absolute top-4 right-4 p-3 bg-white/90 text-red-600 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50"
                                    >
                                        <FiTrash2 />
                                    </button>
                                </div>
                                <div className="p-4">
                                    <p className="text-slate-700 font-medium truncate">{img.caption || 'No caption'}</p>
                                    <p className="text-slate-400 text-xs mt-1">Uploaded: {new Date(img.created_at).toLocaleDateString()}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}

                {/* Upload Modal */}
                <AnimatePresence>
                    {showModal && (
                        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                            <motion.div 
                                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                                className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
                            >
                                <div className="bg-rose-600 p-6 text-white">
                                    <h3 className="text-xl font-bold">Upload New Image</h3>
                                    <p className="text-rose-100 text-sm">Add a high-quality photo to the gallery.</p>
                                </div>
                                <form onSubmit={handleUpload} className="p-8">
                                    <div className="mb-6">
                                        <label className="block text-slate-700 font-semibold mb-2">Select Image</label>
                                        <div className="border-2 border-dashed border-slate-200 rounded-2xl p-8 text-center hover:border-rose-400 transition-colors relative">
                                            <input 
                                                type="file" 
                                                accept="image/*"
                                                className="absolute inset-0 opacity-0 cursor-pointer"
                                                onChange={(e) => setFormData({ ...formData, image: e.target.files[0] })}
                                                required
                                            />
                                            {formData.image ? (
                                                <div className="text-rose-600 font-medium">
                                                    <FiImage className="mx-auto text-3xl mb-2" />
                                                    {formData.image.name}
                                                </div>
                                            ) : (
                                                <div className="text-slate-400">
                                                    <FiUpload className="mx-auto text-3xl mb-2" />
                                                    <p>Click or drag to upload</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="mb-8">
                                        <label className="block text-slate-700 font-semibold mb-2">Caption (Optional)</label>
                                        <input 
                                            type="text" 
                                            placeholder="Enter image description..."
                                            className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-rose-500 outline-none"
                                            value={formData.caption}
                                            onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
                                        />
                                    </div>
                                    <div className="flex gap-4">
                                        <button 
                                            type="button"
                                            onClick={() => setShowModal(false)}
                                            className="flex-1 px-6 py-3 rounded-xl border border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button 
                                            type="submit"
                                            disabled={uploading}
                                            className="flex-1 px-6 py-3 rounded-xl bg-rose-600 text-white font-semibold hover:bg-rose-700 transition-colors disabled:opacity-50"
                                        >
                                            {uploading ? 'Uploading...' : 'Upload Now'}
                                        </button>
                                    </div>
                                </form>
                            </motion.div>
                        </div>
                    )}
                </AnimatePresence>
            </div>
        </DashboardLayout>
    );
};

export default ManageGallery;
